import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ShieldCheck, Image, Video, Zap, Coins, Check, Info, Loader2, ChevronDown, ArrowUp } from 'lucide-react';
import { usePaywallStore } from '@/stores/paywallStore';
import { BillingService, type BillingPlanDto, type BillingPlanFeatureDto } from '@/services/BillingService';
const billing = new BillingService();

/* ─── FAQ Data ────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'How does pricing work?',
    a: 'Both plans give you 4,000 credits per month. Each AI generation costs a certain number of credits depending on the model \u2014 from 1 credit for fast models to ~500 for premium video. The credit cost is always shown before you generate.',
  },
  {
    q: 'What are credits?',
    a: 'Credits are Cascady\u2019s simple unit for AI generation. Cheap image models cost as little as 1 credit, premium video models up to ~500. Both plans include 4,000 credits/month \u2014 enough for hundreds of images or dozens of videos.',
  },
  {
    q: 'Can I buy more credits if I run out?',
    a: 'Credit top-ups are coming soon. For now, your 4,000 monthly credits refresh at the start of each billing cycle.',
  },
  {
    q: 'What happens to my work if I don\u2019t subscribe?',
    a: 'Nothing \u2014 your canvases, images, and videos are safe. You can still view, download, and share everything. You just can\u2019t generate new content until you subscribe.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, cancel anytime with one click. No lock-in, no cancellation fees. Your remaining credits stay active until the end of your billing period.',
  },
  {
    q: 'What AI models are included?',
    a: 'All of them \u2014 30+ image models (Gemini, Seedream, FLUX, Recraft, Ideogram, GPT Image, and more) and 20+ video models (Veo, Sora, Kling, Hailuo, Seedance, and more). Every plan gets access to every model.',
  },
  {
    q: 'What\u2019s the difference between Monthly and Yearly?',
    a: 'Both plans include the same 4,000 credits/month and access to all models. The yearly plan saves you $38 compared to paying monthly \u2014 that\u2019s 2 months free.',
  },
];

/* ─── Model generation data ──────────────────────── */
interface ModelRow {
  name: string;
  type: 'image' | 'video';
  baseCost: number;
  note?: string;
}

const MODEL_DATA: ModelRow[] = [
  // Image models — sorted cheapest first (30 total in app, showing representative selection)
  { name: 'FLUX Schnell',             type: 'image', baseCost: 0.003 },
  { name: 'GPT Image 1.5 Low',        type: 'image', baseCost: 0.009 },
  { name: 'MiniMax Image',            type: 'image', baseCost: 0.01 },
  { name: 'Luma Photon',              type: 'image', baseCost: 0.019 },
  { name: 'FLUX Dev',                 type: 'image', baseCost: 0.025 },
  { name: 'Seedream V4',              type: 'image', baseCost: 0.03 },
  { name: 'Ideogram V3 Turbo',        type: 'image', baseCost: 0.03 },
  { name: 'Qwen Image Edit Plus',     type: 'image', baseCost: 0.03 },
  { name: 'GPT Image 1.5 Medium',     type: 'image', baseCost: 0.034 },
  { name: 'Seedream 5 Lite Edit',     type: 'image', baseCost: 0.035 },
  { name: 'Gemini 2.5 Flash',         type: 'image', baseCost: 0.039 },
  { name: 'Recraft V3',               type: 'image', baseCost: 0.04 },
  { name: 'FLUX Kontext Pro',         type: 'image', baseCost: 0.04 },
  { name: 'Seedream 4.5',             type: 'image', baseCost: 0.04 },
  { name: 'FLUX 2 Flex',              type: 'image', baseCost: 0.06 },
  { name: 'FLUX 1.1 Pro Ultra',       type: 'image', baseCost: 0.06 },
  { name: 'Ideogram V3',              type: 'image', baseCost: 0.06 },
  { name: 'Gemini 3.1 Flash',         type: 'image', baseCost: 0.067, note: '1K res' },
  { name: 'FLUX Kontext Max',         type: 'image', baseCost: 0.08 },
  { name: 'Ideogram V3 Quality',      type: 'image', baseCost: 0.09 },
  { name: 'GPT Image 1.5 High',       type: 'image', baseCost: 0.133 },
  { name: 'Gemini 3 Pro',             type: 'image', baseCost: 0.134, note: '1K res' },
  { name: 'Ideogram V3 Character',    type: 'image', baseCost: 0.20 },
  // Video models — sorted cheapest first (cost = costPerSecond × default clip length)
  { name: 'Seedance 1.0 Fast',        type: 'video', baseCost: 0.25,  note: '5s clip' },
  { name: 'LTX Video 2.0',            type: 'video', baseCost: 0.25,  note: '5s clip' },
  { name: 'Hailuo-02',                type: 'video', baseCost: 0.27,  note: '6s clip' },
  { name: 'Kling 2.5 Turbo',          type: 'video', baseCost: 0.35,  note: '5s clip' },
  { name: 'Sora 2',                   type: 'video', baseCost: 0.40,  note: '4s clip' },
  { name: 'Hailuo-02 Pro',            type: 'video', baseCost: 0.48,  note: '6s clip' },
  { name: 'Seedance 1.5',             type: 'video', baseCost: 0.50,  note: '5s clip' },
  { name: 'Veo 3.1 Fast',             type: 'video', baseCost: 0.60,  note: '4s clip' },
  { name: 'Kling 3.0 Standard',       type: 'video', baseCost: 0.84,  note: '5s clip' },
  { name: 'Kling O3 Standard',        type: 'video', baseCost: 0.84,  note: '5s clip' },
  { name: 'Kling 3.0 Pro',            type: 'video', baseCost: 1.12,  note: '5s clip' },
  { name: 'Kling O3 Pro',             type: 'video', baseCost: 1.12,  note: '5s clip' },
  { name: 'Sora 2 Pro',               type: 'video', baseCost: 1.20,  note: '4s clip' },
  { name: 'Veo 3.1',                  type: 'video', baseCost: 1.60,  note: '4s clip' },
];

const USD_PER_CREDIT = 0.003;
function creditCost(baseCost: number): number {
  return Math.ceil(baseCost / USD_PER_CREDIT);
}
function maxFromBudget(baseCost: number, budgetCredits: number = 4000): number {
  return Math.floor(budgetCredits / creditCost(baseCost));
}

/* ─── Plan presentation config ───────────────────── */
const PLAN_CONFIG: Record<string, { badge?: string; highlight?: boolean; cta: string; perDay?: string }> = {
  plan_monthly: { cta: 'Start Creating', perDay: '4,000 credits/month' },
  monthly: { cta: 'Start Creating', perDay: '4,000 credits/month' },
  plan_yearly: { badge: '2 Months Free \u2014 Save $38', highlight: true, cta: 'Start Creating', perDay: '4,000 credits/month' },
  yearly: { badge: '2 Months Free \u2014 Save $38', highlight: true, cta: 'Start Creating', perDay: '4,000 credits/month' },
};

/* ─── Helpers ────────────────────────────────────── */
function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

function intervalLabel(interval: string): string {
  if (interval === 'month') return '/month';
  if (interval === 'year') return '/year';
  if (interval === 'once') return ' one-time';
  return `/${interval}`;
}

/* ═══════════════════════════════════════════════════
 *  PaywallModal
 * ═══════════════════════════════════════════════════ */
export function PaywallModal() {
  const isOpen = usePaywallStore((s) => s.isModalOpen);
  const paywallVariant = usePaywallStore((s) => s.paywallVariant);
  const closePaywall = usePaywallStore((s) => s.closePaywall);
  const isTrial = paywallVariant === 'trial';
  const isCancelledTrial = paywallVariant === 'cancelled_trial';

  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    billing
      .listPlans()
      .then((data) => {
        const activePlans = data
          .filter((p) => p.isActive && p.key !== 'alpha')
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setPlans(activePlans);
      })
      .catch((err) => console.error('Failed to load plans:', err))
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      previousActiveElement.current?.focus();
      return;
    }
    previousActiveElement.current = document.activeElement as HTMLElement;
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();
    return () => { document.body.style.overflow = origOverflow; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePaywall(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closePaywall]);

  const handleCheckout = useCallback(
    async (planKey: string, interval: 'month' | 'year') => {
      try {
        setCheckoutLoading(planKey);
        const { url } = await billing.createCheckoutSession({ planKey, interval, source: 'paywall' });
        window.location.href = url;
      } catch (err: any) {
        console.error('Checkout error:', err);
        alert(err.message || 'Failed to start checkout');
        setCheckoutLoading(null);
      }
    },
    [],
  );

  // exhausted_trial is handled by ExhaustedTrialModal, not here
  if (!isOpen || paywallVariant === 'exhausted_trial') return null;

  const imageModels = MODEL_DATA.filter(m => m.type === 'image');
  const videoModels = MODEL_DATA.filter(m => m.type === 'video');

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Choose your plan">
      <div className="fixed inset-0 paywall-backdrop" onClick={closePaywall} />

      <div ref={modalRef} tabIndex={-1} className="relative min-h-full flex flex-col outline-none focus:outline-none">
        {/* Close */}
        <button
          onClick={closePaywall}
          className="fixed top-4 right-4 z-[10000] w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
          aria-label="Close pricing"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ── SECTION 1: Plans ── */}
        <section className="paywall-plans-section relative px-4 pt-16 pb-12 sm:px-6 lg:px-8">
          <div className="text-center mb-12 paywall-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white/80 text-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
              {isTrial ? 'No charge during trial' : isCancelledTrial ? 'Subscribe to continue creating' : 'Your canvases are safe'}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">
              {isTrial ? 'Start Your Free Trial' : isCancelledTrial ? 'Your Trial Was Cancelled' : 'Choose Your Plan'}
            </h1>
            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
              {isTrial
                ? 'Try all 60+ AI models free for 7 days. Cancel anytime before the trial ends.'
                : isCancelledTrial
                  ? 'Subscribe to a plan to continue creating with all 30+ image models and 20+ video models.'
                  : 'Unlock unlimited AI creativity. Every plan includes all 30+ image models and 20+ video models.'}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 paywall-stagger-in">
              {plans.map((plan, i) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  config={PLAN_CONFIG[plan.key] ?? { cta: 'Get Started' }}
                  index={i}
                  checkoutLoading={checkoutLoading}
                  onCheckout={handleCheckout}
                  isTrial={isTrial}
                />
              ))}
            </div>
          )}

          {/* Trust signals */}
          <div className="flex items-center justify-center mt-10 text-sm text-white/40 paywall-fade-in" style={{ animationDelay: '0.6s' }}>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              Secure checkout via Stripe
            </span>
          </div>
        </section>

        {/* ── SECTION 2: Model Generation Calculator ── */}
        <section className="relative px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-3">
                What 4,000 credits gets you
              </h2>
              <p className="text-white/50 max-w-xl mx-auto">
                Every plan includes all models. Here's how many generations you get each month.
              </p>
            </div>

            {/* Image table */}
            <div className="paywall-info-card mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Image className="w-5 h-5 text-accent-400" />
                <h3 className="text-base font-semibold text-white">Image Generation</h3>
              </div>
              <p className="text-xs text-white/40 mb-4 ml-7">30+ models available · default resolution · cheapest first</p>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2.5 px-2 text-white/40 font-medium text-xs uppercase tracking-wider">Model</th>
                      <th className="text-right py-2.5 px-2 text-white/40 font-medium text-xs uppercase tracking-wider">Credits</th>
                      <th className="text-right py-2.5 px-2 text-accent-400/60 font-medium text-xs uppercase tracking-wider">Per month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imageModels.map((m, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                        <td className="py-2 px-2">
                          <span className="text-white/80 font-medium">{m.name}</span>
                          {m.note && <span className="text-white/35 text-xs ml-1.5">{m.note}</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-white/50 tabular-nums">{creditCost(m.baseCost).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right text-accent-400/80 font-semibold tabular-nums">{maxFromBudget(m.baseCost).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-white/30 mt-3">
                Showing a selection of models. Credit cost is always displayed before you generate.
              </p>
            </div>

            {/* Video table */}
            <div className="paywall-info-card">
              <div className="flex items-center gap-2 mb-1">
                <Video className="w-5 h-5 text-accent-400" />
                <h3 className="text-base font-semibold text-white">Video Generation</h3>
              </div>
              <p className="text-xs text-white/40 mb-4 ml-7">15+ models available · default settings, no audio · cheapest first</p>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2.5 px-2 text-white/40 font-medium text-xs uppercase tracking-wider">Model</th>
                      <th className="text-right py-2.5 px-2 text-white/40 font-medium text-xs uppercase tracking-wider">Credits</th>
                      <th className="text-right py-2.5 px-2 text-accent-400/60 font-medium text-xs uppercase tracking-wider">Per month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videoModels.map((m, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                        <td className="py-2 px-2">
                          <span className="text-white/80 font-medium">{m.name}</span>
                          {m.note && <span className="text-white/35 text-xs ml-1.5">· {m.note}</span>}
                        </td>
                        <td className="py-2 px-2 text-right text-white/50 tabular-nums">{creditCost(m.baseCost).toLocaleString()}</td>
                        <td className="py-2 px-2 text-right text-accent-400/80 font-semibold tabular-nums">{maxFromBudget(m.baseCost).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-white/30 mt-3">
                Longer clips, higher resolution, and audio increase cost. Exact credits shown before each generation.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 3: Info Cards ── */}
        <section className="relative px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* How Credits Work Card */}
            <div className="paywall-info-card relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-400/40 to-transparent" />
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-accent-400/10 border border-accent-400/20 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-accent-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">How Credits Work</h3>
                  <p className="text-xs text-accent-400/60 font-medium mt-0.5">Simple, predictable AI pricing</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-white/55 leading-relaxed">
                <p>
                  Every plan gives you <strong className="text-white/70">4,000 credits per month</strong>. Each AI generation costs a set number of credits depending on the model {'\u2014'} from just 1 credit for fast models to ~500 for premium video.
                </p>
                <div className="flex gap-3 py-3">
                  <div className="flex-1 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 text-center">
                    <div className="text-lg font-bold text-white/70">~10</div>
                    <div className="text-xs text-white/45 mt-0.5">credits / image</div>
                  </div>
                  <div className="flex-1 rounded-lg bg-accent-400/[0.06] border border-accent-400/15 p-3 text-center">
                    <div className="text-lg font-bold text-accent-400/90">4,000</div>
                    <div className="text-xs text-accent-400/60 mt-0.5">credits / month</div>
                  </div>
                  <div className="flex-1 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 text-center">
                    <div className="text-lg font-bold text-white/70">400+</div>
                    <div className="text-xs text-white/45 mt-0.5">images / month</div>
                  </div>
                </div>
                <p>
                  The exact credit cost is <strong className="text-white/70">shown before you confirm</strong> every generation {'\u2014'} no surprises. Credits refresh at the start of each billing cycle.
                </p>
              </div>
            </div>

            {/* Credit Top-Ups Card */}
            <div className="paywall-info-card relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-400/40 to-transparent" />
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-400/10 border border-green-400/20 flex items-center justify-center shrink-0">
                  <Coins className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Credit Top-Ups</h3>
                  <p className="text-xs text-green-400/60 font-medium mt-0.5">Coming soon</p>
                </div>
              </div>
              <div className="space-y-3 text-sm text-white/55 leading-relaxed">
                <p>
                  Creative work is unpredictable. Some months you{'\u2019'}ll barely use your credits, other months you{'\u2019'}ll burn through them in a week. We get it.
                </p>
                <div className="rounded-lg bg-green-400/[0.06] border border-green-400/15 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/70 font-medium">Top-Ups</span>
                    <span className="text-green-400 font-bold text-lg">Coming Soon</span>
                  </div>
                  <p className="text-xs text-white/50">
                    We{'\u2019'}re building one-click credit top-ups so you never hit a wall mid-project.
                  </p>
                </div>
                <p>
                  For now, your 4,000 credits refresh automatically at the start of each billing cycle. If you need more credits before your cycle resets, upgrading from Monthly to Yearly gives you the same credits at a better price.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 4: FAQ ── */}
        <section className="relative px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-2">
              {FAQ_ITEMS.map((item, i) => (
                <FaqItem key={i} question={item.q} answer={item.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: Bottom CTA ── */}
        <section className="relative px-4 py-16 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-4">
            {isTrial ? 'Ready to try it out?' : isCancelledTrial ? 'Ready to continue?' : 'Ready to create?'}
          </h2>
          <p className="text-white/50 mb-8 max-w-md mx-auto">
            {isTrial
              ? 'Start your 7-day free trial now. No charge until the trial ends.'
              : isCancelledTrial
                ? 'Subscribe to a plan and pick up right where you left off.'
                : 'Start for less than the cost of a coffee per day. Your existing work is always safe.'}
          </p>
          <button
            onClick={() => { document.querySelector('.paywall-plans-section')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="paywall-cta-btn inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-base font-semibold"
          >
            View Plans
            <ArrowUp className="w-4 h-4" />
          </button>
        </section>

        <div className="h-8" />
      </div>

      <PaywallStyles />
    </div>,
    document.body,
  );
}

/* ─── Plan Card ──────────────────────────────────── */
interface PlanCardProps {
  plan: BillingPlanDto;
  config: { badge?: string; highlight?: boolean; cta: string; perDay?: string };
  index: number;
  checkoutLoading: string | null;
  onCheckout: (planKey: string, interval: 'month' | 'year') => void;
  isTrial?: boolean;
}

function PlanCard({ plan, config, index, checkoutLoading, onCheckout, isTrial }: PlanCardProps) {
  const price = plan.prices[0];
  const isHighlight = config.highlight;
  const isLoading = checkoutLoading === plan.key;
  const includedFeatures = plan.features?.filter((f) => f.isIncluded) ?? [];
  const excludedFeatures = plan.features?.filter((f) => !f.isIncluded) ?? [];

  return (
    <div
      className={`paywall-plan-card relative flex flex-col rounded-2xl p-px overflow-visible ${isHighlight ? 'paywall-plan-highlight md:-mt-4 md:mb-0' : ''}`}
      style={{ animationDelay: `${0.15 * index}s` }}
    >
      <div className={`flex flex-col flex-1 rounded-2xl p-6 lg:p-8 ${isHighlight ? 'paywall-card-inner-highlight' : 'paywall-card-inner'}`}>
        {/* Badge — properly centered */}
        {config.badge && (
          <div className="flex justify-center -mt-10 mb-4">
            <div className="px-5 py-1.5 rounded-lg bg-accent-400 text-navy-900 text-xs font-bold tracking-wide uppercase text-center whitespace-nowrap shadow-lg shadow-accent-400/20">
              {config.badge}
            </div>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
          {plan.description && <p className="text-sm text-white/40">{plan.description}</p>}
        </div>

        <div className="mb-6">
          {price ? (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl lg:text-5xl font-bold text-white tracking-tight">{formatPrice(price.unitAmount)}</span>
                <span className="text-base text-white/40">{intervalLabel(price.interval)}</span>
              </div>
              {isTrial && price && (
                <p className="text-sm text-green-400/80 mt-1">7-day free trial, then {formatPrice(price.unitAmount)}{intervalLabel(price.interval)}</p>
              )}
              {!isTrial && config.perDay && <p className="text-sm text-accent-400/80 mt-1">{config.perDay}</p>}
            </>
          ) : (
            <div className="text-4xl font-bold text-white">Free</div>
          )}
        </div>

        <button
          onClick={() => price && onCheckout(plan.key, price.interval as 'month' | 'year')}
          disabled={!price || isLoading}
          className={`w-full py-3 px-6 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isHighlight ? 'paywall-cta-btn' : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'}`}
        >
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirecting...
            </span>
          ) : isTrial ? 'Start Free Trial' : config.cta}

        </button>

        <div className="my-6 border-t border-white/10" />

        <ul className="space-y-3 flex-1">
          {includedFeatures.map((f, i) => <FeatureRow key={i} feature={f} included />)}
          {excludedFeatures.map((f, i) => <FeatureRow key={`ex-${i}`} feature={f} included={false} />)}
        </ul>
      </div>
    </div>
  );
}

/* ─── Feature Row with Rich Tooltip ──────────────── */
function FeatureRow({ feature, included }: { feature: BillingPlanFeatureDto; included: boolean }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<'above' | 'below'>('above');
  const rowRef = useRef<HTMLLIElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const hasTooltip = !!feature.tooltipHtml;

  const handleMouseEnter = () => {
    if (!hasTooltip) return;
    if (rowRef.current) {
      const rect = rowRef.current.getBoundingClientRect();
      setTooltipPos(rect.top > 300 ? 'above' : 'below');
    }
    timeoutRef.current = window.setTimeout(() => setShowTooltip(true), 200);
  };
  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowTooltip(false);
  };
  const handleToggle = () => {
    if (!hasTooltip) return;
    if (!showTooltip && rowRef.current) {
      const rect = rowRef.current.getBoundingClientRect();
      setTooltipPos(rect.top > 300 ? 'above' : 'below');
    }
    setShowTooltip(prev => !prev);
  };

  // Close tooltip on outside tap (mobile)
  React.useEffect(() => {
    if (!showTooltip) return;
    const close = (e: PointerEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) setShowTooltip(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [showTooltip]);

  return (
    <li ref={rowRef} className={`flex items-start gap-3 text-sm relative ${hasTooltip ? 'cursor-help select-none' : ''}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleToggle}>
      {included ? (
        <Check className="w-4 h-4 text-accent-400 mt-0.5 shrink-0" />
      ) : (
        <X className="w-4 h-4 text-white/20 mt-0.5 shrink-0" />
      )}
      <span className={`${included ? 'text-white/70' : 'text-white/30'} ${hasTooltip ? 'border-b border-dotted border-white/20' : ''}`}>
        {feature.text}
      </span>
      {hasTooltip && (
        <Info className="w-3 h-3 text-white/20 mt-0.5 shrink-0 ml-auto" />
      )}

      {hasTooltip && showTooltip && (
        <div className={`paywall-tooltip absolute left-0 z-50 w-72 p-3.5 rounded-xl bg-navy-800/95 backdrop-blur-xl border border-white/15 shadow-2xl shadow-black/40 text-xs text-white/60 leading-relaxed ${tooltipPos === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
          <div dangerouslySetInnerHTML={{ __html: feature.tooltipHtml! }} />
          <div className={`absolute left-6 w-2 h-2 rotate-45 bg-navy-800/95 border-white/15 ${tooltipPos === 'above' ? 'bottom-[-5px] border-r border-b' : 'top-[-5px] border-l border-t'}`} />
        </div>
      )}
    </li>
  );
}

/* ─── FAQ Item ───────────────────────────────────── */
function FaqItem({ question, answer, isOpen, onToggle }: { question: string; answer: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left text-white/80 hover:text-white hover:bg-white/5 transition-all">
        <span className="text-sm font-medium pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-white/30 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="px-5 pb-4 text-sm text-white/50 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

/* ─── Scoped Styles ──────────────────────────────── */
function PaywallStyles() {
  return (
    <style>{`
      .paywall-backdrop {
        background: linear-gradient(135deg, rgba(13,16,23,0.97) 0%, rgba(29,41,57,0.98) 40%, rgba(21,28,40,0.97) 70%, rgba(13,16,23,0.98) 100%);
        backdrop-filter: blur(40px) saturate(1.2);
      }
      .paywall-plan-card {
        background: linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%);
        transition: transform 0.25s ease, box-shadow 0.25s ease;
      }
      .paywall-plan-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 60px -15px rgba(0,0,0,0.4);
      }
      .paywall-plan-highlight {
        background: linear-gradient(135deg, rgba(200,212,90,0.3) 0%, rgba(168,180,74,0.15) 50%, rgba(200,212,90,0.3) 100%);
      }
      .paywall-card-inner {
        background: linear-gradient(180deg, rgba(29,41,57,0.9) 0%, rgba(21,28,40,0.95) 100%);
      }
      .paywall-card-inner-highlight {
        background: linear-gradient(180deg, rgba(35,47,63,0.95) 0%, rgba(21,28,40,0.98) 100%);
      }
      .paywall-cta-btn {
        background: linear-gradient(135deg, #c8d45a 0%, #a8b44a 100%);
        color: #1d2939;
        box-shadow: 0 4px 20px rgba(200,212,90,0.3), 0 2px 8px rgba(0,0,0,0.15);
        transition: all 0.2s ease;
      }
      .paywall-cta-btn:hover {
        box-shadow: 0 6px 30px rgba(200,212,90,0.45), 0 4px 12px rgba(0,0,0,0.2);
        transform: translateY(-1px);
      }
      .paywall-cta-btn:active { transform: translateY(0); }
      .paywall-info-card {
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 16px;
        padding: 24px;
        transition: border-color 0.25s ease;
      }
      .paywall-info-card:hover { border-color: rgba(255,255,255,0.10); }
      .paywall-tooltip {
        animation: tooltipFadeIn 0.15s ease-out;
      }
      .paywall-tooltip strong {
        color: rgba(255,255,255,0.85);
        font-weight: 600;
      }
      @keyframes tooltipFadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .paywall-fade-in { animation: paywallFadeIn 0.5s ease-out both; }
      .paywall-stagger-in > * { animation: paywallSlideUp 0.5s ease-out both; }
      .paywall-stagger-in > *:nth-child(1) { animation-delay: 0.1s; }
      .paywall-stagger-in > *:nth-child(2) { animation-delay: 0.2s; }
      .paywall-stagger-in > *:nth-child(3) { animation-delay: 0.3s; }
      @keyframes paywallFadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes paywallSlideUp {
        from { opacity: 0; transform: translateY(24px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  );
}
